import React, { useEffect, useRef, useState } from "react";
import Keyboard from "react-simple-keyboard";
import { Keyboard as KeyboardIcon } from "lucide-react";
import "react-simple-keyboard/build/css/index.css";
import "../../styles/OnScreenKeyboard.css";

const OnScreenKeyboard = ({
  isVisible = true,
  isDisabled = false,
  onKeyPress,
  isEmailMode = false,
  isDogTagMode = false,
}) => {
  const keyboard = useRef(null);
  const activeInputRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  /* ----------  Focus management  ---------- */
  useEffect(() => {
    if (!isVisible) return;

    const handleFocusIn = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        activeInputRef.current = e.target;
        keyboard.current?.setInput(e.target.value);
      }
    };

    document.addEventListener("focusin", handleFocusIn);

    // If an input is already focused when the keyboard mounts
    const current = document.activeElement;
    if (
      current &&
      (current.tagName === "INPUT" || current.tagName === "TEXTAREA")
    ) {
      activeInputRef.current = current;
      keyboard.current?.setInput(current.value);
    }

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [isVisible]);

  /* ----------  Helpers  ---------- */
  const inputSupportsSelection = (el) =>
    typeof el.setSelectionRange === "function" &&
    // no cursor for these types
    !["email", "number", "date", "time", "month", "week"].includes(el.type);

  /* ----------  Layout configurations  ---------- */
  const getLayouts = () => {
    if (isDogTagMode) {
      return {
        default: [
          "1 2 3 4 5 6 7 8 9 0 {bksp}",
          "Q W E R T Y U I O P",
          "A S D F G H J K L {enter}",
          "Z X C V B N M . - ' {space}",
          "/ & ( ) \" # *",
        ],
      };
    } else if (isEmailMode) {
      return {
        default: [
          "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} q w e r t y u i o p {bksp}",
          "a s d f g h j k l {enter}",
          "{shift} z x c v b n m {shift}",
          "@ {bigcom} .org .net {space}",
          "{gmail} {outlook} {yahoo} {hotmail}",
        ],
        shift: [
          "! @ # $ % ^ & * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P {bksp}",
          "A S D F G H J K L {enter}",
          "{shift} Z X C V B N M {shift}",
          "@ {bigcom} .ORG .NET {space}",
          "{gmail} {outlook} {yahoo} {hotmail}",
        ],
      };
    } else {
      return {
        default: [
          "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} q w e r t y u i o p [ ] \\",
          "{lock} a s d f g h j k l ; ' {enter}",
          "{shift} z x c v b n m , . / {shift}",
          "{space}",
        ],
        shift: [
          "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P { } |",
          '{lock} A S D F G H J K L : " {enter}',
          "{shift} Z X C V B N M < > ? {shift}",
          "{space}",
        ],
      };
    }
  };

  const getDisplayMap = () => {
    const baseDisplay = {
      "{bksp}": "backspace",
      "{enter}": "enter",
      "{shift}": "shift",
      "{tab}": "tab",
      "{lock}": "caps",
      "{space}": " ",
    };

    if (isDogTagMode) {
      return {
        ...baseDisplay,
        // Dog tag mode uses simple display
      };
    } else if (isEmailMode) {
      return {
        ...baseDisplay,
        "{bigcom}": ".com",
        ".org": ".org",
        ".net": ".net",
        ".ORG": ".ORG",
        ".NET": ".NET",
        "@": "@",
        "{gmail}": "@gmail.com",
        "{outlook}": "@outlook.com",
        "{yahoo}": "@yahoo.com",
        "{hotmail}": "@hotmail.com",
      };
    }

    return baseDisplay;
  };

  /* ----------  Keyboard → real input sync  ---------- */
  const handleKeyboardChange = (value) => {
    if (!activeInputRef.current || isDisabled) return;
    const el = activeInputRef.current;

    // Native value update so React onChange fires
    const setter = Object.getOwnPropertyDescriptor(
      el.constructor.prototype,
      "value"
    ).set;
    setter.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));

    // Keep cursor at end if supported
    if (inputSupportsSelection(el)) {
      requestAnimationFrame(() =>
        el.setSelectionRange(value.length, value.length)
      );
    }
  };

  const handleKeyPress = (button) => {
    if (isDisabled) return;

    // Handle email domain shortcuts (only for email mode)
    if (isEmailMode && !isDogTagMode) {
      const emailSuffixes = {
        "{gmail}": "@gmail.com",
        "{yahoo}": "@yahoo.com",
        "{hotmail}": "@hotmail.com",
        "{outlook}": "@outlook.com",
      };

      if (emailSuffixes[button]) {
        if (activeInputRef.current) {
          const currentValue = keyboard.current.getInput();
          const newValue = currentValue + emailSuffixes[button];
          keyboard.current.setInput(newValue);
          handleKeyboardChange(newValue);
        }
        return;
      }
    }

    // Handle shift and caps lock (not applicable to dog tag mode)
    if (!isDogTagMode && (button === "{shift}" || button === "{lock}")) {
      const layoutName =
        keyboard.current.options.layoutName === "default" ? "shift" : "default";
      keyboard.current.setOptions({ layoutName });
    } else if (button === "{enter}" && typeof onKeyPress === "function") {
      onKeyPress(button);
    }
  };

  /* ----------  Render  ---------- */
  if (!isVisible) return null;

  const toggleKeyboard = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getKeyboardWrapperClass = () => {
    let classes = "keyboard-wrapper";
    if (isDisabled) classes += " disabled";
    if (isEmailMode) classes += " email-mode";
    if (isDogTagMode) classes += " dogtag-mode";
    if (isCollapsed) classes += " hidden";
    return classes;
  };



  return (
    <div className="keyboard-toggle-container">
      <button
        className="keyboard-toggle-button"
        onClick={toggleKeyboard}
        aria-label={isCollapsed ? "Show keyboard" : "Hide keyboard"}
      >
        <KeyboardIcon size={24} />
        <span>{isCollapsed ? "Show" : "Hide"}</span>
      </button>
      
      <div
        className={getKeyboardWrapperClass()}
        onMouseDownCapture={(e) => e.preventDefault()}
        onTouchStartCapture={(e) => e.preventDefault()}
      >
        <Keyboard
          keyboardRef={(r) => (keyboard.current = r)}
          layoutName="default"
          layout={getLayouts()}
          display={getDisplayMap()}
          theme="hg-theme-default hg-layout-default myTheme"
          mergeDisplay
          onChange={handleKeyboardChange}
          onKeyPress={handleKeyPress}
        />
      </div>
    </div>
  );
};

export default OnScreenKeyboard;
